import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { PricingStore } from "../s25.event.pricing.store";
import { S25PricingUtil } from "../../s25.pricing.util";
import { S25Date } from "../../../../util/core/s25.date";
import { S25InvoiceViewComponent } from "../invoice/s25.invoice.component";
import { AccountSummaryMetrics } from "./account-summary-metrics";
import { S25PricingPrintInvoiceComponent } from "../print-invoice/s25.pricing.print.invoice.component";

@TypeManagerDecorator("s25-ng-account-summary")
@Component({
    selector: "s25-ng-account-summary",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DecimalPipe, S25InvoiceViewComponent, S25PricingPrintInvoiceComponent],
    host: { id: "pricing-account-summary", role: "region", "aria-label": "Invoice summary" },
    template: `
        <div class="acct-head">
            <h4 class="acct-title">Invoice Summary</h4>
            @if (canEdit()) {
                <button type="button" class="aw-button aw-button--outline" (click)="createInvoice()">
                    Create Invoice
                </button>
            }
            <div class="acct-range" role="group" aria-label="Summary range">
                @for (option of rangeOptions; track option.value) {
                    <button
                        type="button"
                        class="aw-button"
                        [class.aw-button--primary]="range() === option.value"
                        [class.aw-button--outline]="range() !== option.value"
                        [attr.aria-pressed]="range() === option.value"
                        (click)="range.set(option.value)"
                    >
                        {{ option.label }}
                    </button>
                }
            </div>
        </div>

        <div class="acct-print">
            <span class="acct-print-label" aria-hidden="true">Print Invoice</span>
            <s25-ng-pricing-print-invoice [defaultToEventInvoice]="true" />
        </div>

        <div class="acct-tile-grid">
            <div class="acct-tile">
                <div class="acct-tile--label">Total billed</div>
                <div class="acct-tile--value">{{ currency(tiles().totalBilled) }}</div>
            </div>
            <div class="acct-tile">
                <div class="acct-tile--label">Paid</div>
                <div class="acct-tile--value">{{ currency(tiles().paid) }}</div>
            </div>
            <div class="acct-tile">
                <div class="acct-tile--label">Outstanding</div>
                <div class="acct-tile--value">{{ currency(tiles().outstanding) }}</div>
            </div>
            <div class="acct-tile">
                <div class="acct-tile--label">Overdue</div>
                <div class="acct-tile--value" [class.acct-tile--value-alert]="tiles().overdue > 0">
                    {{ currency(tiles().overdue) }}
                </div>
            </div>
            <div class="acct-tile">
                <div class="acct-tile--label">Open invoices</div>
                <div class="acct-tile--value">{{ tiles().openInvoices }}</div>
            </div>
        </div>

        <div class="acct-chart-grid">
            <div class="acct-chart-card">
                <div class="acct-chart-card--label">Amount paid toward total</div>
                <div class="acct-collect-headline">
                    <span class="acct-collect-pct">{{ collectionPaidPct() | number: "1.0-0" }}%</span>
                    <span class="acct-chart-card--sub">of {{ currency(collection().total) }} collected</span>
                </div>
                <div class="acct-collect-bar">
                    <div class="acct-collect-bar--paid" [style.width.%]="collectionPaidPct()"></div>
                    <div class="acct-collect-bar--current" [style.width.%]="collectionCurrentPct()"></div>
                    <div class="acct-collect-bar--overdue" [style.width.%]="collectionOverduePct()"></div>
                </div>
                <div class="acct-collect-legend">
                    <div class="acct-collect-legend--row">
                        <span class="acct-collect-swatch acct-collect-swatch--paid"></span>
                        <span>Paid</span>
                        <span class="acct-collect-legend--amt">{{ currency(collection().paid) }}</span>
                        <span class="acct-collect-legend--pct">{{ collectionPaidPct() | number: "1.0-0" }}%</span>
                    </div>
                    <div class="acct-collect-legend--row">
                        <span class="acct-collect-swatch acct-collect-swatch--current"></span>
                        <span>Current</span>
                        <span class="acct-collect-legend--amt">{{ currency(collection().current) }}</span>
                        <span class="acct-collect-legend--pct">{{ collectionCurrentPct() | number: "1.0-0" }}%</span>
                    </div>
                    <div class="acct-collect-legend--row">
                        <span class="acct-collect-swatch acct-collect-swatch--overdue"></span>
                        <span>Overdue</span>
                        <span class="acct-collect-legend--amt">{{ currency(collection().overdue) }}</span>
                        <span class="acct-collect-legend--pct">{{ collectionOverduePct() | number: "1.0-0" }}%</span>
                    </div>
                </div>
            </div>

            <div class="acct-chart-card">
                <div class="acct-chart-card--label">Billed vs paid by organization</div>
                <div class="acct-chart-card--sub">Billed to date, paid {{ paidRangeLabel() }}</div>
                @if (orgBars().length) {
                    <div class="acct-org-bars">
                        @for (bar of orgBars(); track bar.orgId) {
                            <div class="acct-org-bar">
                                <div class="acct-org-bar--head">
                                    <span class="acct-org-bar--name">{{ bar.orgName }}</span>
                                    <span class="acct-org-bar--amt">{{ currency(bar.billed) }}</span>
                                </div>
                                <div
                                    class="acct-org-bar--track"
                                    [style.width.%]="percentOf(bar.billed, orgBarsMaxBilled())"
                                >
                                    <div
                                        class="acct-org-bar--fill"
                                        [style.width.%]="percentOf(bar.paid, bar.billed)"
                                    ></div>
                                </div>
                                <div class="acct-org-bar--foot">
                                    {{ currency(bar.paid) }} paid ({{
                                        percentOf(bar.paid, bar.billed) | number: "1.0-0"
                                    }}%)
                                </div>
                            </div>
                        }
                    </div>
                } @else {
                    <p class="acct-empty">Nothing billed or paid on this account.</p>
                }
            </div>

            <div class="acct-chart-card">
                <div class="acct-chart-card--label">Paid vs balance by {{ bucket() }}</div>
                <div class="acct-chart-card--sub">{{ rangeLabel() }}</div>
                <div class="acct-week-bars">
                    @for (bar of periodBars(); track bar.key) {
                        <div class="acct-week-bar">
                            <div
                                class="acct-week-bar--paid"
                                [style.height.%]="percentOf(bar.paid, periodBarsMax())"
                                [attr.title]="currency(bar.paid) + ' paid'"
                            ></div>
                            <div
                                class="acct-week-bar--balance"
                                [style.height.%]="percentOf(bar.balance, periodBarsMax())"
                                [attr.title]="currency(bar.balance) + ' balance'"
                            ></div>
                        </div>
                    }
                </div>
                <div class="acct-week-labels">
                    @for (bar of periodBars(); track bar.key) {
                        <div class="acct-week-label">{{ bar.label }}</div>
                    }
                </div>
                <div class="acct-collect-legend">
                    <div class="acct-collect-legend--row">
                        <span class="acct-collect-swatch acct-collect-swatch--paid"></span>
                        <span>Paid in {{ bucket() }}</span>
                    </div>
                    <div class="acct-collect-legend--row">
                        <span class="acct-collect-swatch acct-collect-swatch--balance"></span>
                        <span>Balance at {{ bucket() }} end</span>
                    </div>
                </div>
            </div>
        </div>

        <section class="acct-billed-items" aria-labelledby="pricing-billed-items-heading">
            <h4 id="pricing-billed-items-heading">Billed Items</h4>
            @if (referenceInvoice()) {
                <s25-ng-invoice-view [invoice]="referenceInvoice()" [mode]="'billedItems'" />
            } @else {
                <p class="acct-empty">This event has no record of billed items.</p>
            }
        </section>
    `,
    styles: `
        :host {
            display: block;
        }

        .acct-head {
            display: flex;
            align-items: baseline;
            gap: 14px;
            margin-bottom: 14px;
            padding-bottom: 0.5em;
            border-bottom: 2px solid var(--color--border);
            flex-wrap: wrap;
        }

        .acct-range {
            margin-left: auto;
            display: flex;
            gap: 8px;
        }

        .acct-print {
            display: flex;
            flex-direction: column;
            gap: 5px;
            margin-bottom: 14px;
        }

        .acct-print-label {
            font-size: 11px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .acct-empty {
            color: var(--color--text-muted);
        }

        .acct-tile-grid {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 14px;
            margin-bottom: 20px;
        }

        .acct-tile {
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding: 15px 16px;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            border-radius: 4px;
            min-width: 0;
        }

        .acct-tile--label {
            font-size: 11px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .acct-tile--value {
            font-size: 28px;
            line-height: 1;
            font-weight: 600;
        }

        .acct-tile--value-alert {
            color: var(--color--danger);
        }

        .acct-chart-grid {
            display: grid;
            grid-template-columns: 1.1fr 1fr 1.4fr;
            gap: 14px;
            align-items: stretch;
        }

        .acct-chart-card {
            display: flex;
            flex-direction: column;
            gap: 14px;
            padding: 16px 18px;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            border-radius: 4px;
            min-width: 0;
        }

        .acct-chart-card--label {
            font-size: 11px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .acct-chart-card--label + .acct-chart-card--sub {
            margin-top: -12px;
        }

        .acct-chart-card--sub {
            font-size: 13px;
            color: var(--color--text-muted);
        }

        .acct-collect-headline {
            display: flex;
            align-items: baseline;
            gap: 8px;
        }

        .acct-collect-pct {
            font-size: 36px;
            line-height: 1;
            font-weight: 600;
        }

        .acct-collect-bar {
            display: flex;
            height: 22px;
            border: 1px solid var(--color--border);
            overflow: hidden;
        }

        .acct-collect-bar--paid {
            background: var(--color-primary);
        }

        .acct-collect-bar--current {
            background: color-mix(in srgb, var(--color-primary) 30%, transparent);
        }

        .acct-collect-bar--overdue {
            background: repeating-linear-gradient(45deg, var(--color--danger) 0 4px, transparent 4px 8px);
        }

        .acct-collect-legend {
            display: flex;
            flex-direction: column;
            gap: 7px;
        }

        .acct-collect-legend--row {
            display: flex;
            align-items: center;
            gap: 9px;
            font-size: 13px;
            border-top: 1px solid var(--color--border);
            padding-top: 7px;
        }

        .acct-collect-legend--amt {
            margin-left: auto;
            font-weight: 600;
        }

        .acct-collect-legend--pct {
            width: 38px;
            text-align: right;
            color: var(--color--text-muted);
            font-size: 12px;
        }

        .acct-collect-swatch {
            width: 10px;
            height: 10px;
            flex: none;
        }

        .acct-collect-swatch--paid {
            background: var(--color-primary);
        }

        .acct-collect-swatch--current {
            background: color-mix(in srgb, var(--color-primary) 30%, transparent);
        }

        .acct-collect-swatch--overdue {
            background: var(--color--danger);
        }

        .acct-collect-swatch--balance {
            background: color-mix(in srgb, var(--color-primary) 30%, transparent);
            outline: 1px solid var(--color--border);
        }

        .acct-org-bars {
            display: flex;
            flex-direction: column;
            gap: 11px;
        }

        .acct-org-bar {
            display: flex;
            flex-direction: column;
            gap: 5px;
            min-width: 0;
        }

        .acct-org-bar--head {
            display: flex;
            align-items: baseline;
            gap: 8px;
            font-size: 13px;
        }

        .acct-org-bar--name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .acct-org-bar--amt {
            margin-left: auto;
            font-weight: 600;
        }

        .acct-org-bar--track {
            height: 14px;
            border: 1px solid var(--color--border);
            background: color-mix(in srgb, var(--color-primary) 12%, transparent);
        }

        .acct-org-bar--fill {
            height: 100%;
            background: var(--color-primary);
        }

        .acct-org-bar--foot {
            font-size: 12px;
            color: var(--color--text-muted);
        }

        .acct-week-bars {
            display: flex;
            align-items: flex-end;
            gap: 14px;
            height: 150px;
            border-bottom: 1px solid var(--color--border);
        }

        .acct-week-bar {
            flex: 1;
            min-width: 0;
            height: 100%;
            display: flex;
            align-items: flex-end;
            justify-content: center;
            gap: 3px;
        }

        .acct-week-bar--paid {
            width: 14px;
            background: var(--color-primary);
        }

        .acct-week-bar--balance {
            width: 14px;
            background: color-mix(in srgb, var(--color-primary) 30%, transparent);
            border: 1px solid var(--color--border);
        }

        .acct-week-labels {
            display: flex;
            gap: 14px;
        }

        .acct-week-label {
            flex: 1;
            min-width: 0;
            text-align: center;
            font-size: 11px;
            color: var(--color--text-muted);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .acct-billed-items {
            margin-top: 24px;
        }

        .acct-billed-items h4 {
            margin: 0 0 10px;
            padding-bottom: 0.5em;
            border-bottom: 2px solid var(--color--border);
        }

        /* Mobile */
        @media (max-width: 900px) {
            .acct-tile-grid {
                grid-template-columns: repeat(2, 1fr);
            }

            .acct-chart-grid {
                grid-template-columns: 1fr;
            }
        }
    `,
})
export class S25AccountSummaryComponent {
    private readonly pricingStore = inject(PricingStore);

    protected readonly rangeOptions: { value: AccountSummaryMetrics.SummaryRangeOption; label: string }[] = [
        { value: "month", label: "Month" },
        { value: "quarter", label: "Quarter" },
        { value: "ytd", label: "Year to date" },
    ];

    protected readonly canEdit = this.pricingStore.canEditPricing;

    /** The summary shows no invoice of its own, so a new one is opened as soon as it exists. */
    protected createInvoice() {
        this.pricingStore.openInvoiceForm({ mode: "create", selectOnCreate: true });
    }

    protected readonly range = signal<AccountSummaryMetrics.SummaryRangeOption>("ytd");
    protected readonly rangeLabel = computed(() => AccountSummaryMetrics.summaryRangeLabel(this.range()));
    /** Reads mid-sentence, where the range is only what the payments were scoped to. */
    protected readonly paidRangeLabel = computed(() => this.rangeLabel().toLowerCase());

    /** A year of weeks is unreadable as bars, so a year reports by month instead. */
    protected readonly bucket = computed<AccountSummaryMetrics.PeriodBucket>(() =>
        this.range() === "ytd" ? "month" : "week",
    );

    private readonly now = computed(() => S25Date.current());
    private readonly dateRange = computed(() => AccountSummaryMetrics.summaryRange(this.range(), this.now()));

    /** The default set and every invoice the user created - what the account actually bills. */
    private readonly invoices = this.pricingStore.listedInvoices;
    protected readonly referenceInvoice = this.pricingStore.referenceInvoice;

    protected readonly tiles = computed(() => AccountSummaryMetrics.accountSummaryTiles(this.invoices(), this.now()));

    protected readonly collection = computed(() =>
        AccountSummaryMetrics.collectionProgressSplit(this.invoices(), this.now()),
    );
    protected readonly collectionPaidPct = computed(() =>
        AccountSummaryMetrics.percentOf(this.collection().paid, this.collection().total),
    );
    protected readonly collectionCurrentPct = computed(() =>
        AccountSummaryMetrics.percentOf(this.collection().current, this.collection().total),
    );
    protected readonly collectionOverduePct = computed(() =>
        AccountSummaryMetrics.percentOf(this.collection().overdue, this.collection().total),
    );

    protected readonly orgBars = computed(() =>
        AccountSummaryMetrics.orgBilledVsPaid(this.invoices(), this.pricingStore.orgNameById(), this.dateRange()),
    );
    protected readonly orgBarsMaxBilled = computed(() => Math.max(1, ...this.orgBars().map((bar) => bar.billed)));

    protected readonly periodBars = computed(() =>
        AccountSummaryMetrics.bucketedPaidVsBalance({
            payments: AccountSummaryMetrics.accountPayments(this.invoices()),
            totalBilled: this.tiles().totalBilled,
            range: this.dateRange(),
            bucket: this.bucket(),
        }),
    );
    protected readonly periodBarsMax = computed(() =>
        Math.max(1, ...this.periodBars().flatMap((bar) => [bar.paid, bar.balance])),
    );

    protected readonly currency = S25PricingUtil.formatCurrency;
    protected readonly percentOf = AccountSummaryMetrics.percentOf;
}
